import { useState, useMemo, useEffect } from "react";
import { X, Search, LayoutTemplate, Send, Edit3, CheckCircle2, Tag } from "lucide-react";
import type { TemplateItem } from "../../lib/api";

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: TemplateItem[];
  contactName?: string;
  onSelect: (interpolatedText: string, sendImmediately: boolean) => void;
}

export function TemplatePickerModal({
  isOpen,
  onClose,
  templates,
  contactName,
  onSelect,
}: TemplatePickerModalProps) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(null);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});

  // Reset or initialize when modal opens or selected template changes
  useEffect(() => {
    if (isOpen && templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0] ?? null);
    }
  }, [isOpen, templates, selectedTemplate]);

  // Extract variables (e.g. {{name}}, {{1}}, {{order_id}}) from template body and header
  const templateVariables = useMemo(() => {
    if (!selectedTemplate) return [];
    const structure = selectedTemplate.structure || {};
    const fullText = `${structure.header_content || ""} ${structure.body_text || ""}`;
    const matches = fullText.match(/\{\{([^{}]+)\}\}/g) || [];
    const unique = Array.from(new Set(matches.map((m) => m.replace(/[{}]/g, "").trim())));
    return unique;
  }, [selectedTemplate]);

  // Initialize variable values with contactName if variable is 'name' or '1'
  useEffect(() => {
    const initial: Record<string, string> = {};
    templateVariables.forEach((v) => {
      if (v.toLowerCase() === "name" || v === "1") {
        initial[v] = contactName || "";
      } else {
        initial[v] = "";
      }
    });
    setVariableValues(initial);
  }, [templateVariables, contactName]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const categories = ["ALL", "MARKETING", "UTILITY", "SERVICE"];

  const filteredTemplates = templates.filter((t) => {
    const matchesCat = selectedCategory === "ALL" || t.category.toUpperCase() === selectedCategory;
    const q = search.toLowerCase().trim();
    const matchesSearch =
      !q ||
      t.name.toLowerCase().includes(q) ||
      (t.structure?.body_text || "").toLowerCase().includes(q) ||
      (t.structure?.header_content || "").toLowerCase().includes(q);
    return matchesCat && matchesSearch;
  });

  // Generate final interpolated text
  const getInterpolatedText = () => {
    if (!selectedTemplate) return "";
    const structure = selectedTemplate.structure || {};
    let header = structure.header_content || "";
    let body = structure.body_text || "";
    const footer = structure.footer_text || "";

    templateVariables.forEach((v) => {
      const val = variableValues[v] || `{{${v}}}`;
      const reg = new RegExp(`\\{\\{${v}\\}\\}`, "g");
      header = header.replace(reg, val);
      body = body.replace(reg, val);
    });

    const parts = [
      structure.header_type === "text" && header ? header : "",
      body,
      footer ? `_${footer}_` : "",
    ].filter(Boolean);

    return parts.join("\n\n");
  };

  const handleApply = (sendImmediately: boolean) => {
    const text = getInterpolatedText();
    onSelect(text, sendImmediately);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white w-full max-w-4xl h-[85vh] max-h-[720px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* Modal Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shadow-xs">
              <LayoutTemplate className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">QR WhatsApp Message Templates</h3>
              <p className="text-xs text-slate-500">
                Select a pre-approved template, customize placeholders, and send.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body: 2 Columns */}
        <div className="flex-1 flex min-h-0 divide-x divide-slate-200">
          {/* Left Column: Template List & Filters */}
          <div className="w-1/2 flex flex-col h-full bg-white">
            {/* Search & Category Pills */}
            <div className="p-3 border-b border-slate-100 space-y-2.5">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors shrink-0 ${
                      selectedCategory === cat
                        ? "bg-emerald-600 text-white shadow-xs"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-2 space-y-1">
              {filteredTemplates.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400 space-y-1">
                  <p>No templates found.</p>
                  <p className="text-[11px]">Try adjusting your search or category filter.</p>
                </div>
              ) : (
                filteredTemplates.map((t) => {
                  const isSelected = selectedTemplate?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedTemplate(t)}
                      className={`w-full text-left p-3 rounded-xl transition-all flex flex-col gap-1 border ${
                        isSelected
                          ? "bg-emerald-50/80 border-emerald-500 shadow-xs"
                          : "border-transparent hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-900 capitalize">
                          {t.name.replace(/_/g, " ")}
                        </span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                          {t.category}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 line-clamp-2">
                        {t.structure?.body_text || "No body content"}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Column: Template Customization & Live Preview */}
          <div className="w-1/2 flex flex-col h-full bg-slate-50/50">
            {selectedTemplate ? (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Dynamic Variables Form */}
                  {templateVariables.length > 0 && (
                    <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs space-y-2.5">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                        <Tag className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Fill Template Placeholders</span>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {templateVariables.map((v) => (
                          <div key={v} className="flex flex-col gap-1">
                            <label className="text-[11px] font-medium text-slate-600 font-mono">
                              {`{{${v}}}`}
                            </label>
                            <input
                              type="text"
                              value={variableValues[v] || ""}
                              onChange={(e) =>
                                setVariableValues({ ...variableValues, [v]: e.target.value })
                              }
                              placeholder={`Value for ${v}...`}
                              className="w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Live WhatsApp Bubble Preview */}
                  <div className="space-y-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                      Live Chat Preview
                    </span>
                    <div className="bg-[#EFEAE2] p-4 rounded-2xl border border-slate-200/80 shadow-inner flex justify-end">
                      <div className="max-w-[85%] bg-[#D9FDD3] text-slate-900 rounded-2xl rounded-tr-xs p-3 text-xs shadow-xs space-y-1.5">
                        {selectedTemplate.structure?.header_content && (
                          <div className="font-bold text-slate-900 border-b border-black/10 pb-1">
                            {selectedTemplate.structure.header_content.replace(
                              /\{\{([^{}]+)\}\}/g,
                              (_, k) => variableValues[k] || `{{${k}}}`
                            )}
                          </div>
                        )}
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {(selectedTemplate.structure?.body_text || "").replace(
                            /\{\{([^{}]+)\}\}/g,
                            (_, k) => variableValues[k] || `{{${k}}}`
                          )}
                        </p>
                        {selectedTemplate.structure?.footer_text && (
                          <p className="text-[10px] text-slate-500 italic pt-0.5">
                            {selectedTemplate.structure.footer_text}
                          </p>
                        )}
                        <div className="text-[10px] text-slate-400 text-right flex items-center justify-end gap-1 pt-0.5">
                          <span>Just now</span>
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Action Buttons */}
                <div className="p-3.5 bg-white border-t border-slate-200 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => handleApply(false)}
                    className="flex-1 py-2 px-3 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Insert into Composer</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApply(true)}
                    className="flex-1 py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>Send Message Now</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-xs text-slate-400">
                Select a template from the list to preview and customize
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
